/**
 * ScopedAdminLayout — permission-gated sidebar admin shell for a scope
 * (chapter or branch).
 *
 * Resolves the current scope via useScope() (from /c/:chapterSlug/admin/...
 * or /c/:chapterSlug/b/:branchSlug/admin/...) and:
 *
 *   1. Verifies the user holds at least contributor+ at the resolved
 *      scope (admin inheritance from ancestors counts). If not, shows
 *      a "no access" panel instead of rendering child content — this
 *      acts as a guard rail without needing a separate ProtectedRoute.
 *   2. Builds sidebar nav URLs from the resolved scope so the same
 *      layout works for chapter admin (`/c/96/admin/…`) and branch
 *      admin (`/c/96/b/cs/admin/…`) without changes.
 *   3. Hides nav entries whose `requiresAction` the user can't perform
 *      at the resolved scope (via spec 100's `canActOn`).
 *
 * The legacy super-admin `/admin/*` tree under AdminLayout is unchanged.
 */

import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { useScope } from '@/contexts/ScopeContext';
import { useMyMemberships } from '@/hooks/useCan';
import { canActOn, hasRoleAtOrAbove, type Action } from '@/lib/permissions';
import type { ScopeType } from '@/services/api';

interface NavEntry {
  /** Path suffix relative to the layout's base (e.g. 'invitations'). */
  to: string;
  label: string;
  /** Optional gating action — entry hidden if the user can't perform it at the current scope. */
  requiresAction?: Action;
}

const NAV: NavEntry[] = [
  { to: '', label: 'Overview' },
  { to: 'pages', label: 'Pages', requiresAction: 'page:edit' },
  { to: 'gallery', label: 'Gallery', requiresAction: 'gallery:upload' },
  { to: 'events', label: 'Events' },
  { to: 'projects', label: 'Projects' },
  { to: 'invitations', label: 'Invitations', requiresAction: 'chapter:invite' },
  { to: 'join-requests', label: 'Join requests', requiresAction: 'chapter:approve-join-request' },
];

export function ScopedAdminLayout() {
  const { user, isLoading } = useAuth();
  const { memberships, loading: mlLoading } = useMyMemberships();
  const { chapter, branch, loading: scopeLoading, error: scopeError } = useScope();
  const location = useLocation();

  if (isLoading || mlLoading || scopeLoading) {
    return <div className="container mx-auto px-4 py-12 text-white/60">Loading…</div>;
  }
  if (!user) {
    return <div className="container mx-auto px-4 py-12 text-white/60">Sign in to access admin.</div>;
  }
  if (scopeError || !chapter) {
    return <div className="container mx-auto px-4 py-12 text-red-400">{scopeError ?? 'Scope not found'}</div>;
  }

  // Resolve the active scope and ancestors for inheritance checks.
  const target = branch
    ? { scopeType: 'branch' as ScopeType, scopeId: branch.id }
    : { scopeType: 'chapter' as ScopeType, scopeId: chapter.id };
  const ancestors: { scopeType: ScopeType; scopeId: string }[] = branch
    ? [{ scopeType: 'chapter', scopeId: chapter.id }]
    : [];
  // Institution ancestor not surfaced by useScope() today — admin inheritance
  // from institution still works because effectiveScopes() expands it on the
  // server side; the client check below only matters for ancestors useScope
  // happens to know about. We err on the side of *showing* the layout when
  // the chapter check passes (server will 403 on actual mutations).

  // Gate: contributor+ at the current scope (or admin at any ancestor).
  const baseAccess =
    hasRoleAtOrAbove(memberships, target, 'contributor', ancestors) ||
    // Super-admin equivalent: institution-admin always.
    memberships.some((m) => m.scopeType === 'institution' && m.role === 'admin');

  if (!baseAccess) {
    return (
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-2xl font-display font-bold text-white mb-2">Admin access required</h1>
        <p className="text-white/60">
          You need contributor or admin membership at {branch?.displayName ?? branch?.name ?? chapter.displayName} to view this area.
        </p>
      </div>
    );
  }

  const basePath = branch
    ? `/c/${chapter.slug}/b/${branch.slug}/admin`
    : `/c/${chapter.slug}/admin`;

  const visibleNav = NAV.filter((entry) => {
    if (!entry.requiresAction) return true;
    return canActOn(memberships, entry.requiresAction, target, ancestors);
  });

  return (
    <div className="container mx-auto px-4 py-6 grid md:grid-cols-[200px,1fr] gap-6">
      <aside className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-white/40 mb-2">Admin</p>
        <p className="text-xs text-white/60 mb-3 truncate">
          {branch?.displayName ?? branch?.name ?? chapter.displayName}
        </p>
        {visibleNav.map((entry) => {
          const to = entry.to ? `${basePath}/${entry.to}` : basePath;
          return (
            <NavLink
              key={to}
              to={to}
              end={entry.to === ''}
              className={({ isActive }) =>
                `block px-3 py-1.5 rounded text-sm transition-colors ${
                  isActive || location.pathname === to
                    ? 'bg-primary/20 text-white'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`
              }
            >
              {entry.label}
            </NavLink>
          );
        })}
      </aside>
      <main>
        <Outlet />
      </main>
    </div>
  );
}

export default ScopedAdminLayout;
